import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  AlertTriangle, Mic, MicOff, Minimize2, Pause, Phone, PhoneOff, Play, X,
} from 'lucide-react';
import { useDialerStore } from '../../stores/dialerStore';
import { namensKlick, namensTitel } from '../../lib/akteBeimAnruf';
import './PowerDialerSidebar.css';

// Höhe der Anruf-Leiste, wenn das Anruffenster klein gemacht ist — der Dialer
// rückt dann so weit nach oben, dass beide nebeneinander lesbar bleiben statt
// übereinander zu liegen.
const ANRUFLEISTE_HOEHE = 52;

const KLEIN_KEY = 'powerdialer-klein';

function leseKlein(): boolean {
  try { return localStorage.getItem(KLEIN_KEY) === '1'; } catch { return false; }
}

export function PowerDialerSidebar() {
  const {
    status, queue, current, muted, error, settings, globalTest, connecting,
    callWindowCollapsed, setCallWindowCollapsed,
    start, stop, pause, resume, endCurrent, toggleMute, removeFromQueue, clearQueue,
  } = useDialerStore();
  const { pathname } = useLocation();

  // Lilly, 25.08.2026: „Man kann die Termine teilweise nicht beim Telefonieren
  // buchen, weil das kleine Anruffenster unten rechts im Weg ist. Vielleicht
  // könnte man das klein machen? Beim Skript kann ich das — ich meine beim
  // Power Dialer." Das Skript-Fenster ließ sich längst klein machen, dieses
  // hier nicht: es stand fest über der rechten unteren Ecke, also genau über
  // dem Buchen-Knopf der Terminsuche.
  //
  // Die Wahl bleibt erhalten: wer klein gemacht hat, telefoniert meist eine
  // ganze Schicht so — nach einem Neuladen wieder aufgeklappt zu sein, wäre
  // genau die Arbeit, die der Knopf sparen soll.
  const [klein, setKlein] = useState(() => leseKlein());
  const kleinSetzen = (v: boolean) => {
    setKlein(v);
    try { localStorage.setItem(KLEIN_KEY, v ? '1' : '0'); } catch { /* Privatmodus: egal */ }
  };

  if (status === 'idle' && queue.length === 0) return null;

  const inCall = status === 'in-call' || status === 'dialing';
  const close = () => {
    if (inCall) return; // erst auflegen
    if (queue.length > 0 && !confirm('Power Dialer beenden? Die Warteschlange wird geleert.')) return;
    void stop();
  };

  // Das klein gemachte Anruffenster sitzt selbst unten rechts. Solange es dort
  // liegt, beginnt der Dialer darüber.
  const anrufLeisteDa = callWindowCollapsed && (status === 'in-call' || status === 'wrap-up');
  const abstandUnten = anrufLeisteDa ? 16 + ANRUFLEISTE_HOEHE : 16;

  // Der Name führt in die Akte. Liegt die schon hinter dem offenen
  // Anruf-Fenster, räumt der Klick stattdessen das Fenster weg — sonst sieht
  // man von dem Sprung nichts.
  const anrufFensterOffen = (status === 'in-call' || status === 'wrap-up') && !callWindowCollapsed;
  const akteKlick = namensKlick({
    ziel: current && !current.ad_hoc ? `/customers/${current.customer_id}` : '',
    pfad: pathname,
    fensterOffen: anrufFensterOffen,
  });

  if (klein) {
    return (
      <div className="cc-dialer-pill" style={{ bottom: abstandUnten }} aria-label="Power Dialer">
        <button
          className="cc-dialer-pill__open"
          onClick={() => kleinSetzen(false)}
          title="Power Dialer wieder aufklappen"
        >
          <Phone size={14} />
          <span>Power Dialer</span>
          <span className={`cc-dialer__status cc-dialer__status--${status}`}>{statusLabel(status)}</span>
          {queue.length > 0 && <span className="cc-dialer-pill__count">{queue.length} in der Schlange</span>}
          <span className="cc-dialer-pill__hint">↑ aufklappen</span>
        </button>
        {/* Auflegen muss auch klein erreichbar bleiben: beim Wählen ist das
            Skript-Fenster noch gar nicht offen, ein laufender Wählversuch
            wäre sonst nur durch Aufklappen zu stoppen. */}
        {inCall && (
          <button
            className="cc-dialer-pill__hangup"
            onClick={endCurrent}
            title="Auflegen"
            aria-label="Auflegen"
          >
            <PhoneOff size={14} />
          </button>
        )}
      </div>
    );
  }

  const globalActive = globalTest.enabled && !!globalTest.number;
  const agentActive = settings.test_mode_enabled && !!settings.test_mode_override_number;
  const testModeActive = globalActive || agentActive;
  const testNumber = globalActive ? globalTest.number : settings.test_mode_override_number;

  return (
    <aside className="cc-dialer" aria-label="Power Dialer" style={{ bottom: abstandUnten }}>
      <div className="cc-dialer__header">
        <span className="cc-dialer__title">Power Dialer</span>
        <span className={`cc-dialer__status cc-dialer__status--${status}`}>{statusLabel(status)}</span>
        <button
          className="cc-dialer__klein"
          onClick={() => kleinSetzen(true)}
          title="Fenster klein machen — es bleibt unten rechts erreichbar, Warteschlange und Gespräch laufen weiter"
        >
          <Minimize2 size={14} /> klein
        </button>
        <button
          className="cc-dialer__close"
          onClick={close}
          disabled={inCall}
          title={inCall ? 'Erst auflegen, dann schließen' : 'Power Dialer beenden und schließen'}
          aria-label="Power Dialer schließen"
        >
          <X size={16} />
        </button>
      </div>

      {testModeActive && (
        <div style={{
          margin: '8px 0', padding: '8px 10px',
          background: '#fff4e5', color: '#92400e',
          border: '1px solid #f59e0b', borderRadius: 6,
          fontSize: 12, display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <AlertTriangle size={14} />
          <span>
            <strong>TEST-MODUS{globalActive ? ' (global)' : ''}</strong> — alle Anrufe gehen auf{' '}
            <code>{testNumber}</code>
          </span>
        </div>
      )}

      {current && (
        <div className="cc-dialer__current">
          {/* Der Name führt in die Kundenakte. Destiny, 08.09.2026: „… sodass
              es sich öffnet, spart mir die Zeit, die Kunden im System zu
              suchen während des Calls." Das Gespräch läuft dabei weiter —
              Dialer und Anruf-Fenster liegen über der Seite. */}
          <div className="cc-dialer__name">
            {current.ad_hoc ? current.full_name : (
              <Link
                className="cc-dialer__akte"
                to={`/customers/${current.customer_id}`}
                onClick={(e) => {
                  if (akteKlick !== 'fenster-wegraeumen') return;
                  e.preventDefault();
                  setCallWindowCollapsed(true);
                }}
                title={namensTitel(akteKlick)}
              >
                {current.full_name}
              </Link>
            )}
          </div>
          <div className="cc-dialer__meta">
            {testModeActive ? (
              <>
                <s>{current.phone_number}</s> → {testNumber}
              </>
            ) : (
              current.phone_number
            )}
            {' • '}{current.school_name ?? '—'}
          </div>
          <div className="cc-dialer__meta">{current.status}</div>
        </div>
      )}

      {error && <div className="cc-dialer__error">{error}</div>}

      <div className="cc-dialer__controls">
        {status === 'idle' || status === 'ready' || status === 'wrap-up' ? (
          <button className="cc-dialer__btn cc-dialer__btn--primary" onClick={() => void start()} disabled={connecting}>
            <Phone size={16} /> {connecting ? 'verbindet…' : 'Start'}
          </button>
        ) : status === 'paused' ? (
          <button className="cc-dialer__btn cc-dialer__btn--primary" onClick={resume}>
            <Play size={16} /> Fortsetzen
          </button>
        ) : (
          <button className="cc-dialer__btn" onClick={pause}>
            <Pause size={16} /> Pause
          </button>
        )}

        <button
          className="cc-dialer__btn"
          onClick={toggleMute}
          disabled={status !== 'in-call'}
          aria-pressed={muted}
        >
          {muted ? <MicOff size={16} /> : <Mic size={16} />} {muted ? 'Unmute' : 'Mute'}
        </button>

        <button
          className="cc-dialer__btn cc-dialer__btn--danger"
          onClick={endCurrent}
          disabled={status !== 'in-call' && status !== 'dialing'}
        >
          <PhoneOff size={16} /> Auflegen
        </button>
      </div>

      <div className="cc-dialer__queue">
        <div className="cc-dialer__queue-header">
          <span>Warteschlange ({queue.length})</span>
          {queue.length > 0 && (
            <button className="cc-dialer__link" onClick={clearQueue}>Leeren</button>
          )}
        </div>
        <ul>
          {queue.slice(0, 20).map((lead) => (
            <li key={lead.customer_id}>
              {lead.ad_hoc ? (
                <span className="cc-dialer__queue-name">{lead.full_name}</span>
              ) : (
                <Link
                  className="cc-dialer__queue-name cc-dialer__akte"
                  to={`/customers/${lead.customer_id}`}
                  title="Kundenakte öffnen"
                >
                  {lead.full_name}
                </Link>
              )}
              <span className="cc-dialer__queue-phone">{lead.phone_number}</span>
              <button
                className="cc-dialer__remove"
                aria-label="Aus Queue entfernen"
                onClick={() => removeFromQueue(lead.customer_id)}
              >
                <X size={14} />
              </button>
            </li>
          ))}
        </ul>
      </div>
    </aside>
  );
}

function statusLabel(s: string) {
  switch (s) {
    case 'idle': return 'inaktiv';
    case 'ready': return 'bereit';
    case 'dialing': return 'wählt…';
    case 'in-call': return 'im Gespräch';
    case 'wrap-up': return 'Nachbereitung';
    case 'paused': return 'pausiert';
    default: return s;
  }
}
